import React, { useEffect, useState } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../misc/Card";
import { Button } from "../../misc/Button";
import { Elements } from "@stripe/react-stripe-js";
import { Appearance, loadStripe } from "@stripe/stripe-js";
import PaymentInfo from "./PaymentInfo";
import LoadingScreen from "../../../screens/LoadingScreen";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import { auth } from "../../../firebase/firebase-config";
import { getIdToken } from "firebase/auth";
import axios from "axios";
import { Dialog, DialogContent } from "../../misc/Dialog";
import ChangePlan from "./ChangePlan";
import { CurrentPlan } from "../types";
import { useNavigate } from "react-router-dom";
import { CardDetailsDto } from "./dto/CardDetails.dto";
import CardPreview from "./CardPreview";
import CurrentPlanCard from "./CurrentPlanCard";
import { CreditCard } from "lucide-react";
import { getAuthHeader } from "../../../utils/authHeader";
import { useImpersonation } from "../../../hooks/useImpersonation";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY!);
const appearance: Appearance = {
    theme: "stripe",
};

const Subscription = () => {
    const navigate = useNavigate();
    const [clientSecret, setClientSecret] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPlan, setCurrentPlan] = useState<CurrentPlan>();
    const [changePlansOpen, setChangePlansOpen] = useState<boolean>(false);
    const [card, setCard] = useState<CardDetailsDto>();

    // Lazy-load payment form
    const [showPaymentForm, setShowPaymentForm] = useState<boolean>(false);
    const [loadingPaymentForm, setLoadingPaymentForm] =
        useState<boolean>(false);

    // Success UI (inline)
    const [pmUpdateSuccess, setPmUpdateSuccess] = useState<boolean>(false);

    // Idle timeout duration in milliseconds (5 minutes)
    const IDLE_TIMEOUT = 5 * 60 * 1000;

    useEffect(() => {
        let idleTimer: NodeJS.Timeout;

        const resetIdleTimer = () => {
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
                toast.info(
                    <CustomToast
                        message="You have been inactive for a while. Redirecting to Settings."
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
                navigate("/settings");
            }, IDLE_TIMEOUT);
        };

        // Events that will reset the idle timer
        const events = [
            "mousemove",
            "keydown",
            "mousedown",
            "touchstart",
            "scroll",
        ];

        events.forEach((event) => {
            window.addEventListener(event, resetIdleTimer);
        });

        // Initialize the idle timer when the component mounts
        resetIdleTimer();

        // Clean up event listeners and timer when the component unmounts
        return () => {
            events.forEach((event) => {
                window.removeEventListener(event, resetIdleTimer);
            });
            if (idleTimer) clearTimeout(idleTimer);
        };
    }, [navigate, IDLE_TIMEOUT]);

    const fetchSetupIntent = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("User not authenticated");
            const token = await getIdToken(currentUser, true);

            const response = await axios.post(
                `${backendUrl}/stripe/setup-intent`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setClientSecret(response.data.clientSecret);
            return true;
        } catch (error) {
            console.error("Error creating payment intent:", error);
            toast.error(
                <CustomToast
                    message="Error creating payment intent. Please try again."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            return false;
        }
    };

    const fetchCurrentPlan = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const headers = await getAuthHeader();
            const selectedPlan: CurrentPlan = await axios
                .get(`${backendUrl}/settings/current-plan`, {
                    headers,
                })
                .then((response) => response.data);
            setCurrentPlan(selectedPlan);
        } catch (error) {
            toast.error(
                <CustomToast
                    message="Failed to fetch current plan. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        }
    };

    const fetchCardDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const headers = await getAuthHeader();
            const cardDetails: CardDetailsDto = await axios
                .get(`${backendUrl}/stripe/card-details`, {
                    headers,
                })
                .then((response) => response.data);
            setCard(cardDetails);
        } catch (error) {
            toast.error(
                <CustomToast
                    message="Failed to fetch card details. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        }
    };

    useEffect(() => {
        const initialize = async () => {
            try {
                setLoading(true);
                await fetchCurrentPlan();
                await fetchCardDetails();
            } catch (error) {
                console.error("Initialization error:", error);
            } finally {
                setLoading(false);
            }
        };
        initialize();
    }, []);

    const handleShowPaymentForm = async () => {
        setPmUpdateSuccess(false);
        setLoadingPaymentForm(true);
        const ok = await fetchSetupIntent();
        if (ok) setShowPaymentForm(true);
        setLoadingPaymentForm(false);
    };

    const handleHidePaymentForm = () => {
        // Unmount Elements to avoid keeping a stale/used client secret around.
        setShowPaymentForm(false);
        setClientSecret("");
    };

    // Called by PaymentInfo AFTER a successful setup confirmation
    const handlePaymentSuccess = () => {
        // Clear the form
        setClientSecret("");
        // Close the form
        setShowPaymentForm(false);
        // Refresh card details
        fetchCardDetails();
        // Show inline success message
        setPmUpdateSuccess(true);
    };

    const isImpersonated = useImpersonation();

    return (
        <div className="space-y-6 px-4 md:px-8">
            {loading ? (
                <LoadingScreen />
            ) : (
                <div className="pb-4">
                    <h2 className="text-2xl font-bold text-primary mt-2">
                        Subscription Information
                    </h2>

                    <Dialog
                        open={changePlansOpen}
                        onOpenChange={setChangePlansOpen}
                    >
                        <DialogContent className="w-full p-0" wide={true}>
                            <ChangePlan
                                setChangePlansOpen={setChangePlansOpen}
                                fetchCurrentPlan={fetchCurrentPlan}
                                status={currentPlan?.status}
                            />
                        </DialogContent>
                    </Dialog>

                    <CurrentPlanCard
                        currentPlan={currentPlan}
                        onChangePlan={() => setChangePlansOpen(true)}
                        onNavigateToHelp={() => {
                            navigate("/help", {
                                state: { tab: "contact" },
                            });
                        }}
                        onBackToSettings={() => navigate("/settings")}
                        isImpersonated={isImpersonated}
                    />

                    <Card className="w-full max-w-4xl mx-auto">
                        <CardHeader>
                            <CardTitle>Payment Method</CardTitle>
                            <CardDescription className="text-center">
                                Update your payment details
                            </CardDescription>
                        </CardHeader>

                        {/* Card preview */}
                        {!showPaymentForm && !loadingPaymentForm && (
                            <CardContent className="space-y-4">
                                {/* Without a card, CardPreview renders
                                    placeholder digits and a hardcoded "Active"
                                    that read as a real card on file. */}
                                {card?.last4 ? (
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                        <CardPreview
                                            card={card}
                                            status="Active"
                                        />
                                    </div>
                                ) : (
                                    <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="p-4 bg-gray-100 rounded-full">
                                                <CreditCard className="w-8 h-8 text-gray-400" />
                                            </div>
                                            <p className="text-gray-600">
                                                No payment method on file
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        )}

                        <CardContent className="space-y-4 mb-2">
                            {/* Inline success banner (appears where form renders) */}
                            {pmUpdateSuccess && !showPaymentForm && (
                                <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-800 text-center">
                                    Payment method updated successfully.
                                </div>
                            )}

                            {/* Trigger button to lazy-load Elements */}
                            {!showPaymentForm ? (
                                <div className="flex justify-center">
                                    <Button
                                        onClick={handleShowPaymentForm}
                                        disabled={loadingPaymentForm || isImpersonated}
                                        className="w-full md:w-auto"
                                    >
                                        {loadingPaymentForm
                                            ? "Loading…"
                                            : "Change Card"}
                                    </Button>
                                </div>
                            ) : null}

                            {/* Render Elements only after user clicks */}
                            {showPaymentForm && clientSecret ? (
                                <>
                                    <Elements
                                        stripe={stripePromise}
                                        options={{ clientSecret, appearance }}
                                    >
                                        {/* Pass success callback down */}
                                        <PaymentInfo
                                            onSuccess={handlePaymentSuccess}
                                        />
                                    </Elements>

                                    <div className="flex justify-center pt-2">
                                        <Button
                                            variant="secondary"
                                            onClick={handleHidePaymentForm}
                                            className="w-full md:w-auto"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </>
                            ) : showPaymentForm && !clientSecret ? (
                                <p className="text-center text-red-500">
                                    Unable to load payment information. Please
                                    try again later.
                                </p>
                            ) : null}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default Subscription;
